<template>
    <Head :title="`Gallery · ${screen}`" />
    <GuidedSetupChrome label="Coaching setup gallery" :show-overview="isOverview">
        <template #overview>
            <ContextSummary :steps="steps" :context="context" :completed-count="completedCount" :readings="overviewReadings" />
        </template>

        <template #sections>
            <BaseSectionNav label="Coaching setup sections" :items="navItems" :model-value="activeSection" />
        </template>

        <template #step>
            <IdentityStep v-if="screen === 'identity'" embedded returning :prefill="F.IDENTITY_PREFILL" :busy="false" :initial-manages-people="true" />

            <IntroStep v-else-if="screen === 'intro'" embedded :first-name="F.IDENTITY_PREFILL.first_name" :manages-people="manager" />

            <ContextRingsStep v-else-if="screen === 'context_rings'" embedded :context="context" />

            <CalendarConnectStep v-else-if="screen === 'calendar_connect'" embedded :calendar="F.CALENDAR" :organization-name="F.ORGANIZATION_NAME" :busy="false" />

            <ThinkingScreen v-else-if="screen === 'scanning'" embedded v-bind="THINKING_SCREENS.scan" />

            <ThinkingScreen v-else-if="screen === 'sharpening'" embedded v-bind="THINKING_SCREENS.readings" />

            <!-- Give the wheel an archetype so the gallery plays the landing rather than spinning forever. -->
            <ThinkingScreen v-else-if="screen === 'scoring'" embedded v-bind="THINKING_SCREENS.perspective" :land="F.PERSPECTIVE_RESULT.letters" />

            <ThinkingScreen v-else-if="screen === 'writing_profile'" embedded v-bind="THINKING_SCREENS.profile" />

            <!-- Ratings off throughout: the gallery's readings are fixtures, so rating one would 404. -->
            <CalendarPayoffStep
                v-else-if="screen === 'calendar_payoff'"
                embedded
                :stats="F.SCAN_STATS"
                :readings="F.FIRST_PASS_READINGS"
                :readings-settled="true"
                :show-rating="false" />

            <AssessmentIntroStep
                v-else-if="screen === 'assessment_intro'"
                embedded
                :stats="F.SCAN_STATS"
                :manages-people="manager"
                :question-count="F.ASSESSMENT.questions.length"
                :calendar-skipped="false" />

            <AssessmentStep v-else-if="screen === 'assessment'" embedded :assessment="F.ASSESSMENT" :busy="false" />

            <PerspectiveResultStep v-else-if="screen === 'perspective_result'" embedded :result="F.PERSPECTIVE_RESULT" :calendar-connected="true" />

            <SharpenedReadingsStep
                v-else-if="screen === 'sharpened'"
                embedded
                :readings="F.SHARPENED_READINGS"
                :settled="true"
                :available-access-levels="ACCESS_LEVELS"
                :calendar-connected="true"
                :show-rating="false" />

            <ProfileIntroStep v-else-if="screen === 'profile_intro'" embedded :first-name="F.IDENTITY_PREFILL.first_name" :manages-people="manager" />

            <ProfilePublishStep
                v-else-if="screen === 'profile'"
                embedded
                :preview="F.PROFILE_PREVIEW"
                :available-access-levels="ACCESS_LEVELS"
                :profile-sharer-names="F.SCAN_STATS.profile_sharer_names"
                :generation-failed="false"
                :busy="false" />

            <p v-else class="unknown">No screen named “{{ screen }}”.</p>
        </template>
    </GuidedSetupChrome>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
/**
 * Dev-only via calendar_first/urls.py. Fixtures avoid variable LLM results; bypass GuidedSetupPage because
 * its runner calls live endpoints. Select screens with ?screen=; use ?manager=0 for non-manager variants.
 */
import { Head } from "@inertiajs/vue3";
import AssessmentIntroStep from "~vue/CalendarFirstOnboarding/AssessmentIntroStep.vue";
import AssessmentStep from "~vue/CalendarFirstOnboarding/AssessmentStep.vue";
import CalendarConnectStep from "~vue/CalendarFirstOnboarding/CalendarConnectStep.vue";
import CalendarPayoffStep from "~vue/CalendarFirstOnboarding/CalendarPayoffStep.vue";
import ContextRingsStep from "~vue/CalendarFirstOnboarding/ContextRingsStep.vue";
import IdentityStep from "~vue/CalendarFirstOnboarding/IdentityStep.vue";
import IntroStep from "~vue/CalendarFirstOnboarding/IntroStep.vue";
import PerspectiveResultStep from "~vue/CalendarFirstOnboarding/PerspectiveResultStep.vue";
import ProfileIntroStep from "~vue/CalendarFirstOnboarding/ProfileIntroStep.vue";
import ProfilePublishStep from "~vue/CalendarFirstOnboarding/ProfilePublishStep.vue";
import SharpenedReadingsStep from "~vue/CalendarFirstOnboarding/SharpenedReadingsStep.vue";
import ThinkingScreen from "~vue/CalendarFirstOnboarding/ThinkingScreen.vue";
import { THINKING_SCREENS } from "~vue/CalendarFirstOnboarding/thinkingScreens";
import type { AccessLevel } from "~vue/CalendarFirstOnboarding/types";
import BaseSectionNav from "~vue/components/BaseSectionNav.vue";
import ContextSummary from "~vue/ProfileSetup/ContextSummary.vue";
import * as F from "~vue/ProfileSetup/dev/galleryFixtures";
import GuidedSetupChrome from "~vue/ProfileSetup/GuidedSetupChrome.vue";
import { SETUP_SECTION_ICONS } from "~vue/ProfileSetup/setupSections";
import type { ContextSnapshot, SetupStepId } from "~vue/ProfileSetup/types";
import { computed } from "vue";

const {
    screen,
    context,
    done_steps: doneStepIds,
    manager = true,
    done = false,
} = defineProps<{ screen: string; context: ContextSnapshot; done_steps: SetupStepId[]; manager?: boolean; done?: boolean }>();

const ACCESS_LEVELS: AccessLevel[] = ["private", "invited_org_members", "organization"];

/*
 * The server derives completed steps and ring scores together; independent fixtures previously showed
 * Calendar incomplete beside a 100% team ring.
 */
const doneSteps = computed(() => new Set<SetupStepId>(doneStepIds));

const overviewReadings = computed(() => (done ? F.SHARPENED_READINGS.readings : []));

const steps = computed(() => F.SETUP_STEPS_MIXED.map((step) => ({ ...step, done: doneSteps.value.has(step.id) })));
const completedCount = computed(() => steps.value.filter((step) => step.done).length);

const isOverview = computed(() => screen === "overview");

const SCREEN_SECTION: Record<string, SetupStepId> = {
    identity: "identity",
    intro: "identity",
    context_rings: "identity",
    calendar_connect: "calendar",
    scanning: "calendar",
    calendar_payoff: "calendar",
    assessment_intro: "perspective",
    assessment: "perspective",
    scoring: "perspective",
    perspective_result: "perspective",
    sharpened: "perspective",
    profile_intro: "publish",
    profile: "publish",
};

const activeSection = computed(() => SCREEN_SECTION[screen] ?? undefined);

/* Mirrors `blockedBy` in `useProfileSetup`: the gallery is where the locked rail gets looked at. */
const navItems = computed(() =>
    steps.value.map((step) => {
        const locked = step.id === "publish" && !doneSteps.value.has("perspective") && !step.done;
        return {
            id: step.id,
            label: step.label,
            icon: SETUP_SECTION_ICONS[step.id],
            completed: step.done,
            disabled: locked,
            note: locked ? "Complete Perspectives first" : undefined,
            accessibleLabel: step.label,
        };
    }),
);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .unknown {
        @apply body text-ink-muted;
    }
}
</style>
